/**
 * The route table, and the sidebar built from it.
 *
 * The sidebar is PRD v3.2 section 5's, in Mohit's own names, and it has two
 * levels only (day 33): the sections and, inside each, its features. The one
 * third level, CV Review or Cover Letter Review, is a choice inside
 * Corporate's Expert Review page and never a sidebar row. test/routes.test.ts
 * fails if the sidebar gains a third level or an entry outside the list.
 *
 *   Dashboard
 *   Corporate     Expert Review, Expert Advice
 *   Academic      Expert Review, Knowledge Base
 *   Research      Writing Assistant, Plagiarism, AI Detection, Citation
 *   Academy       Courses, Publications
 *   (foot)        Subscriptions, then Settings and the account
 *
 * These are real paths, not the prototype's hash routes, which broke every
 * in-page anchor. Pages serves index.html for any path it has no file for,
 * and router.ts takes it from there. A feature lives under its section's
 * path, /research/plagiarism, because two features share the name Expert
 * Review. A route owns its path and everything under it, so the choice inside
 * Corporate's Expert Review is /corporate/expert-review/cv.
 *
 * Access is per route and nowhere else. A student route renders inside the app
 * frame and only for a live session; a public route renders on its own, with no
 * frame, whatever the session says, because an employer checking a certificate
 * has no account and should not be asked for one.
 *
 * Day 33 took out what PRD v3.2 moved or dropped: Law Firm, which is the
 * Standing Lawyers Internship Portal's now, an application of its own (PRD
 * section 5.2); the Helpdesk and the search page, which are in no version of
 * the v3 PRD. Their addresses are not found, because nothing stands there.
 */

import type { ComponentType } from "preact";
import type { IconName } from "../ui/icons";
import * as pages from "./pages";

export type Access = "student" | "public";

export interface PageProps {
  /** Path segments after the route's own, decoded. /settings/security gives ["security"]. */
  readonly params: readonly string[];
  readonly query: URLSearchParams;
}

export type SectionId = "corporate" | "academic" | "research" | "academy";

export type RouteId =
  | "dashboard"
  | "corporate-review"
  | "advice"
  | "paper-review"
  | "knowledge"
  | "writing"
  | "plagiarism"
  | "ai-detection"
  | "citation"
  | "courses"
  | "publications"
  | "subscriptions"
  | "settings"
  | "history"
  | "verify";

export interface RouteDef {
  readonly id: RouteId;
  readonly path: string;
  /** The sidebar label, which is also the page's title. */
  readonly title: string;
  /** The section it sits under in the sidebar, which the page names over its title. */
  readonly section?: SectionId;
  readonly access: Access;
  readonly page: ComponentType<PageProps>;
}

const S: Access = "student";

export const ROUTES: readonly RouteDef[] = [
  { id: "dashboard", path: "/", title: "Dashboard", access: S, page: pages.Dashboard },
  {
    id: "corporate-review",
    path: "/corporate/expert-review",
    title: "Expert Review",
    section: "corporate",
    access: S,
    page: pages.CorporateReview,
  },
  {
    id: "advice",
    path: "/corporate/expert-advice",
    title: "Expert Advice",
    section: "corporate",
    access: S,
    page: pages.ExpertAdvice,
  },
  {
    id: "paper-review",
    path: "/academic/expert-review",
    title: "Expert Review",
    section: "academic",
    access: S,
    page: pages.PaperReview,
  },
  {
    id: "knowledge",
    path: "/academic/knowledge-base",
    title: "Knowledge Base",
    section: "academic",
    access: S,
    page: pages.KnowledgeBase,
  },
  {
    id: "writing",
    path: "/research/writing-assistant",
    title: "Writing Assistant",
    section: "research",
    access: S,
    page: pages.WritingAssistant,
  },
  {
    id: "plagiarism",
    path: "/research/plagiarism",
    title: "Plagiarism",
    section: "research",
    access: S,
    page: pages.Plagiarism,
  },
  {
    id: "ai-detection",
    path: "/research/ai-detection",
    title: "AI Detection",
    section: "research",
    access: S,
    page: pages.AiDetection,
  },
  {
    id: "citation",
    path: "/research/citation",
    title: "Citation",
    section: "research",
    access: S,
    page: pages.Citation,
  },
  {
    id: "courses",
    path: "/academy/courses",
    title: "Courses",
    section: "academy",
    access: S,
    page: pages.Courses,
  },
  {
    id: "publications",
    path: "/academy/publications",
    title: "Publications",
    section: "academy",
    access: S,
    page: pages.Publications,
  },
  {
    id: "subscriptions",
    path: "/subscriptions",
    title: "Subscriptions",
    access: S,
    page: pages.Subscriptions,
  },
  { id: "settings", path: "/settings", title: "Settings", access: S, page: pages.Settings },
  // Every paper and search on one page (Mohit, 30 October 2026), at the sidebar's foot.
  { id: "history", path: "/history", title: "History", access: S, page: pages.History },
  { id: "verify", path: "/verify", title: "Verify a certificate", access: "public", page: pages.Verify },
];

export interface SidebarSection {
  readonly id: SectionId;
  readonly label: string;
  readonly icon: IconName;
  readonly ids: readonly RouteId[];
}

/** The sidebar, top to bottom: the first row, the four sections, then the foot. */
export const SIDEBAR: {
  readonly top: readonly { readonly id: RouteId; readonly icon: IconName }[];
  readonly sections: readonly SidebarSection[];
  readonly foot: readonly { readonly id: RouteId; readonly icon: IconName }[];
} = {
  top: [{ id: "dashboard", icon: "dashboard" }],
  sections: [
    { id: "corporate", label: "Corporate", icon: "building", ids: ["corporate-review", "advice"] },
    { id: "academic", label: "Academic", icon: "book", ids: ["paper-review", "knowledge"] },
    { id: "research", label: "Research", icon: "pen", ids: ["writing", "plagiarism", "ai-detection", "citation"] },
    { id: "academy", label: "Academy", icon: "cap", ids: ["courses", "publications"] },
  ],
  // History above Subscriptions (Mohit, 30 October 2026), when Recents left the sidebar.
  foot: [
    { id: "history", icon: "clock" },
    { id: "subscriptions", icon: "card" },
    { id: "settings", icon: "gear" },
  ],
};

export function sectionOf(id: SectionId): SidebarSection {
  const section = SIDEBAR.sections.find((candidate) => candidate.id === id);
  if (!section) throw new Error(`No section is registered as ${id}.`);
  return section;
}

/**
 * Old addresses that still work. The routes' names before day 33, so a link
 * copied out of an email, or a habit, lands somewhere real rather than on
 * "not found", and a section's own name opens its first feature.
 */
export const REDIRECTS: Readonly<Record<string, string>> = {
  "/dashboard": "/",
  // Create your account, to a browser that is already signed in (App.tsx).
  "/create-account": "/",
  "/citations": "/research/citation",
  "/ai-detector": "/research/ai-detection",
  "/aicheck": "/research/ai-detection",
  "/corporate": "/corporate/expert-review",
  "/academic": "/academic/expert-review",
  "/research": "/research/writing-assistant",
  "/academy": "/academy/courses",
};

const BY_ID = new Map(ROUTES.map((route) => [route.id, route]));

export function routeById(id: RouteId): RouteDef {
  const route = BY_ID.get(id);
  if (!route) throw new Error(`No route is registered as ${id}.`);
  return route;
}

export type Match =
  | { readonly kind: "route"; readonly route: RouteDef; readonly params: readonly string[] }
  | { readonly kind: "redirect"; readonly to: string }
  | { readonly kind: "not_found" };

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // A malformed escape is a segment nobody could have meant. Keeping it raw
    // lets the page say "not found" instead of the router throwing.
    return segment;
  }
}

const segmentsOf = (path: string) => path.split("/").filter((part) => part.length > 0);

/** Routes by how many segments their path has, most first, so the longest match wins. */
const BY_DEPTH = [...ROUTES].filter((route) => route.path !== "/").sort((a, b) => segmentsOf(b.path).length - segmentsOf(a.path).length);

/**
 * Which route a path belongs to.
 *
 * A route owns its path and everything under it, so /settings/security is
 * Settings with params ["security"]. The dashboard owns "/" exactly, or it
 * would own everything. A trailing slash is the same page, redirected to the
 * one spelling so a page is never cached, bookmarked or reported twice.
 */
export function matchPath(pathname: string): Match {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.replace(/\/+$/, "") || "/" : pathname;
  if (path !== pathname) return { kind: "redirect", to: path };

  const redirect = REDIRECTS[path];
  if (redirect) return { kind: "redirect", to: redirect };

  const segments = segmentsOf(path);
  if (segments.length === 0) return { kind: "route", route: routeById("dashboard"), params: [] };

  for (const route of BY_DEPTH) {
    const own = segmentsOf(route.path);
    if (own.length <= segments.length && own.every((part, at) => part === segments[at])) {
      return { kind: "route", route, params: segments.slice(own.length).map(decode) };
    }
  }
  return { kind: "not_found" };
}
